import React, { useState, useEffect, useCallback } from 'react';
import { summaryAPI } from '../utils/api';

/**
 * SendSummaryPanel
 * Shown in the Leader Dashboard for team leaders and admins.
 * Lets them preview the Adaptive Card content, check send status,
 * and trigger sending the summary to the Teams workflow.
 *
 * Props:
 *   date    - YYYY-MM-DD string (currently selected date)
 *   teamId  - numeric team id (required for admins; leaders can pass undefined)
 */
const SendSummaryPanel = ({ date, teamId }) => {
  const [preview, setPreview]       = useState(null);   // full preview payload
  const [status, setStatus]         = useState(null);   // { sentToTeams, sentAt }
  const [loading, setLoading]       = useState(false);
  const [sending, setSending]       = useState(false);
  const [error, setError]           = useState('');
  const [sendResult, setSendResult] = useState(null);   // { success, message/error }
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [unsubmittedMembers, setUnsubmittedMembers] = useState([]);

  // ── Load status & preview whenever date/teamId change ──────────────────
  const loadStatus = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    setError('');
    setSendResult(null);
    try {
      const res = await summaryAPI.status(date, teamId || undefined);
      setStatus(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load summary status');
    } finally {
      setLoading(false);
    }
  }, [date, teamId]);

  const loadPreview = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    setError('');
    try {
      const res = await summaryAPI.preview(date, teamId || undefined);
      setPreview(res.data);
      setShowPreview(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [date, teamId]);

  useEffect(() => {
    setPreview(null);
    setShowPreview(false);
    setSendResult(null);
    loadStatus();
  }, [date, teamId, loadStatus]);

  // ── Send ────────────────────────────────────────────────────────────────
  const handleSend = async () => {
    setSending(true);
    setError('');
    setSendResult(null);
    try {
      const res = await summaryAPI.send(date, teamId || undefined, false);

      if (res.data.requiresConfirmation) {
        setUnsubmittedMembers(res.data.unsubmittedMembers || []);
        setShowConfirmDialog(true);
        return;
      }

      setSendResult({ success: true, message: res.data.message, memberCount: res.data.memberCount });
      // Refresh status after successful send
      const statusRes = await summaryAPI.status(date, teamId || undefined);
      setStatus(statusRes.data);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to send summary to Teams';
      setSendResult({ success: false, message: msg });
    } finally {
      setSending(false);
    }
  };

  const handleConfirmSend = async () => {
    setSending(true);
    setError('');
    setSendResult(null);
    try {
      const res = await summaryAPI.send(date, teamId || undefined, true);
      setSendResult({ success: true, message: res.data.message, memberCount: res.data.memberCount });
      setShowConfirmDialog(false);
      setUnsubmittedMembers([]);

      const statusRes = await summaryAPI.status(date, teamId || undefined);
      setStatus(statusRes.data);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to send summary to Teams';
      setSendResult({ success: false, message: msg });
    } finally {
      setSending(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  const formatDateTime = (dt) =>
    dt ? new Date(dt).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }) : '—';

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-5 py-4 border-b flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">Send to Microsoft Teams</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Sends an Adaptive Card summary to the configured workflow
          </p>
        </div>
        {/* Send status badge */}
        {!loading && status && (
          <span className={`text-xs font-medium px-3 py-1 rounded-full ${
            status.sentToTeams
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-600'
          }`}>
            {status.sentToTeams ? '✅ Sent' : '⏳ Not sent'}
          </span>
        )}
      </div>

      <div className="px-5 py-4 space-y-4">
        {/* Already sent info */}
        {status?.sentToTeams && (
          <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-3">
            Summary was sent on <strong>{formatDateTime(status.sentAt)}</strong>.
            You can resend below if needed.
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
            {error}
          </div>
        )}

        {/* Send result */}
        {sendResult && (
          <div className={`text-sm rounded p-3 border ${
            sendResult.success
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            {sendResult.success
              ? `✅ ${sendResult.message} (${sendResult.memberCount} member report(s) included)`
              : `❌ ${sendResult.message}`}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={loadPreview}
            disabled={loading}
            className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            {loading ? 'Loading...' : showPreview ? 'Refresh Preview' : 'Preview Card'}
          </button>

          <button
            onClick={handleSend}
            disabled={sending || loading}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {sending ? 'Sending...' : status?.sentToTeams ? 'Resend to Teams' : 'Send to Teams'}
          </button>
        </div>

        {/* Preview panel */}
        {showPreview && preview && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Preview header */}
            <div className="bg-gray-50 px-4 py-2 flex items-center justify-between border-b">
              <span className="text-xs font-semibold text-gray-600 uppercase">
                Adaptive Card Preview — {preview.memberCount} member(s)
              </span>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600 text-lg leading-none"
              >
                &times;
              </button>
            </div>

            {/* Simulated Teams card */}
            <div className="p-4 bg-white space-y-4 max-h-96 overflow-y-auto">
              {/* Card title block */}
              <div className="border-l-4 border-blue-500 pl-3">
                <p className="text-lg font-bold text-blue-700">Team {preview.teamName}</p>
                <p className="text-xs text-gray-500">Report Date: {preview.date}</p>
              </div>

              {/* Per-member report blocks */}
              {preview.reports.map((r, i) => (
                <div key={i} className="bg-gray-50 rounded p-3 space-y-2">
                  <p className="font-semibold text-gray-800">{r.full_name}</p>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-500 font-semibold uppercase mb-1">Yesterday</p>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {r.yesterday_text || <span className="italic text-gray-400">N/A</span>}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-semibold uppercase mb-1">Today</p>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {r.today_text || <span className="italic text-gray-400">N/A</span>}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {preview.reports.length === 0 && (
                <p className="text-gray-400 text-sm italic text-center py-4">
                  No submitted reports for this date yet.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-bold text-gray-800">Confirm Send Summary</h2>

              <p className="text-gray-700 text-sm">
                The following members have not submitted a report:
              </p>

              <div className="bg-gray-50 rounded p-3 max-h-48 overflow-y-auto">
                <ul className="space-y-2">
                  {unsubmittedMembers.map(member => (
                    <li key={member.id} className="flex items-start">
                      <span className="text-gray-400 mr-2">•</span>
                      <div>
                        <p className="text-gray-800 font-medium">{member.name}</p>
                        <p className="text-gray-600 text-xs">{member.email}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-gray-700 font-semibold text-sm">
                This action will mark them as <span className="text-orange-600">Today: Off</span> and <span className="text-orange-600">Yesterday: N/A</span>.
              </p>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowConfirmDialog(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmSend}
                  disabled={sending}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                >
                  {sending ? 'Sending...' : 'Confirm & Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SendSummaryPanel;
